import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { convertArrayToCSV } from 'convert-array-to-csv';
import FileSaver from 'file-saver';
import { intersection, isEmpty, sortBy } from 'lodash/fp';

import {
    AGE_KEY,
    AGE_MAX_KEY,
    AGE_MIN_KEY,
    CAUSE_ID_KEY,
    GENDER_ID_KEY,
    LOCATION_ID_KEY,
    MEASURE_ID_KEY,
    METRIC_ID_KEY,
    ROUND_ID_KEY,
    YEAR_KEY,
} from '@portal/common/models/data-key';
import { DataCollectionResource, DataFilters, DataType } from '@portal/common/types';
import { localizeRecords } from '@portal/common/utility/chart-data-helpers';
import formatDateWithFormat from '@portal/common/utility/formatting/formatDateWithFormat';

import api from '../../../api';
import config from '../../../config';
import _ from '../../../locale';
import { localizeDataColumn } from '../../../models/data-column-label';
import { METHODOLOGY_PATH } from '../../../router/paths';
import { getSelectedDataProject } from '../../../store/data-explorer/selectors';
import { intersectRefinementFiltersWithGranularity } from '../utils';
import { NO_BOUND_VALUE } from './CustomAgeGroupSelector';
import { AgeRange, DATE_FORMAT } from './ExportSingleAgeEstimatesModal';
import { AGE_GROUP_SEPARATOR } from './SavedAgeGroups';

export default (
    selectedFilters: DataFilters,
    currentAgeGroup: AgeRange,
    selectedDataTypes: DataType[],
    supportedDataTypes: DataType[],
    selectedDataCollection: DataCollectionResource,
    reportGranularity: DataFilters,
    isSingleAgesReport: boolean,
    savedAgeGroups: string[],
    selectedMetrics: number[],
    supportedMetrics: number[],
    supportedAgeRange: {
        min: number;
        max: number;
    }
) => {
    const selectedDataProject = useSelector(getSelectedDataProject);
    const [inProgress, setInProgress] = useState(false);
    const [errors, setErrors] = useState<{
        message: string;
        fields?: Record<string, string>;
    } | null>(null);

    const saveDataAsCsv = useCallback(
        ({ filename, response: { records, columns } }) => {
            const columnsOrder = [
                METRIC_ID_KEY,
                CAUSE_ID_KEY,
                MEASURE_ID_KEY,
                LOCATION_ID_KEY,
                GENDER_ID_KEY,
                AGE_KEY,
                YEAR_KEY,
            ];
            const sortIndexes = columnsOrder
                .map((key) => columns.indexOf(key))
                .filter((idx) => idx !== -1);

            const localizedData = {
                columns: columns.map((column) =>
                    localizeDataColumn(column, selectedDataTypes, selectedDataProject, true)
                ),
                records: sortBy(sortIndexes, localizeRecords(columns, records, _)),
            };

            const citation = _('ihme_citation_text', {
                date: formatDateWithFormat(new Date(), DATE_FORMAT),
            });
            const methodologyLink = config.clientUrl + METHODOLOGY_PATH;
            let data = convertArrayToCSV(localizedData.records, { header: localizedData.columns });
            data += `\n"${citation}${methodologyLink}"`;

            const name = filename + '.csv';
            const file = new Blob([data], {
                type: 'text/plain',
            });
            FileSaver.saveAs(file, name);
        },
        [selectedDataTypes, selectedDataProject]
    );

    const fetchReport = useCallback(async () => {
        setErrors(null);
        setInProgress(true);

        const ageGroupsToQuery =
            !isSingleAgesReport && !isEmpty(savedAgeGroups)
                ? savedAgeGroups.map((ageGroup) =>
                      ageGroup
                          .split(AGE_GROUP_SEPARATOR)
                          .map((age) => (age === NO_BOUND_VALUE ? NO_BOUND_VALUE : Number(age)))
                  )
                : [currentAgeGroup];

        const queryFn = isSingleAgesReport
            ? api.data.getSingleYearAgeData
            : api.data.getCustomAgeGroupData;

        try {
            const response = {
                columns: [],
                records: [],
            };

            const fetchData = async (payload) => {
                const { columns, records } = await queryFn(payload);

                response.columns = columns;
                response.records = response.records.concat(records);
            };

            const queryAndStoreData = async () => {
                const refinedFilters = intersectRefinementFiltersWithGranularity(
                    selectedFilters,
                    reportGranularity
                );

                for (const ageGroup of ageGroupsToQuery) {
                    const [ageMin, ageMax] = ageGroup;

                    const filters: DataFilters & {
                        age_min?: (number | string)[];
                        age_max?: (number | string)[];
                    } = {
                        ...refinedFilters,
                        age_min: [ageMin],
                        age_max: [ageMax],
                    };

                    const payload = {
                        filters,
                        dataType: intersection(selectedDataTypes, supportedDataTypes)[0],
                        resourceId: selectedDataCollection?.id,
                    };

                    if (isSingleAgesReport || isEmpty(selectedMetrics)) {
                        await fetchData(payload);
                    } else {
                        const metrics = supportedMetrics.filter((metricId) =>
                            selectedMetrics.includes(metricId)
                        );

                        const isGbd2023Round =
                            (selectedFilters?.[ROUND_ID_KEY] as number[])?.[0] ===
                            config.gbd2023RoundId;
                        const hasNoBounds = ageMin === 0 || ageMax === NO_BOUND_VALUE;
                        const hasRateAndNumber =
                            metrics.includes(config.rateMetricId) &&
                            metrics.includes(config.numberMetricId);

                        const filteredMetrics =
                            isGbd2023Round && hasNoBounds && hasRateAndNumber
                                ? [config.numberMetricId]
                                : metrics;

                        const groupFilters: DataFilters & {
                            age_min?: (number | string)[];
                            age_max?: (number | string)[];
                        } = { ...refinedFilters };

                        groupFilters[METRIC_ID_KEY] = filteredMetrics;

                        if (ageMin !== 0) {
                            groupFilters[AGE_MIN_KEY] = [ageMin];
                        }

                        if (ageMax !== NO_BOUND_VALUE) {
                            groupFilters[AGE_MAX_KEY] = [ageMax];
                        }

                        await fetchData({
                            ...payload,
                            filters: groupFilters,
                        });
                    }
                }
            };

            await queryAndStoreData();

            const filename = _(
                isSingleAgesReport
                    ? 'export_single_age_file_name'
                    : 'export_custom_age_band_file_name'
            );
            saveDataAsCsv({ filename, response });
            return true;
        } catch (e) {
            setErrors(e);
            return false;
        } finally {
            setInProgress(false);
        }
    }, [
        currentAgeGroup,
        selectedFilters,
        selectedDataCollection,
        isSingleAgesReport,
        savedAgeGroups,
        selectedDataTypes,
        supportedDataTypes,
        reportGranularity,
        selectedMetrics,
        supportedMetrics,
        supportedAgeRange,
    ]);

    return { inProgress, errors, fetchReport, setErrors };
};
